// frontend/src/services/api/modules/tratamientos.js
import { API_URL } from '../config';
import { getAuthHeaders, fetchWrapper } from '../utils/fetchWrapper';

/**
 * Genera un preview de los recordatorios sin crearlos
 * @param {Array} medicamentos - Lista de medicamentos
 * @returns {Promise<Object>}
 */
export const generarPreviewRecordatorios = async (medicamentos) => {
  try {
    const headers = await getAuthHeaders();
    const resp = await fetchWrapper(`${API_URL}/api/tratamientos/preview`, {
      method: "POST",
      headers,
      body: JSON.stringify({ medicamentos })
    });

    const result = await resp.json();
    if (!resp.ok) {
      return { error: result.error || "Error al generar preview" };
    }
    return result;
  } catch (error) {
    console.error("Error en generarPreviewRecordatorios:", error);
    return { error: "Error de conexión con el servidor" };
  }
};

/**
 * Crea un tratamiento con todos sus recordatorios
 * @param {Object} data - { objetoId, entradaExpedienteId, medicamentos }
 * @returns {Promise<Object>}
 */
export const crearTratamiento = async (data) => {
  try {
    const headers = await getAuthHeaders();
    const resp = await fetchWrapper(`${API_URL}/api/tratamientos/crear`, {
      method: "POST",
      headers,
      body: JSON.stringify(data)
    });

    const result = await resp.json();
    if (!resp.ok) {
      // Devolver todo el resultado para incluir tratamientoExistenteId en caso de duplicado
      return {
        error: result.error || "Error al crear tratamiento",
        message: result.message,
        tratamientoExistenteId: result.tratamientoExistenteId
      };
    }
    return result;
  } catch (error) {
    console.error("Error en crearTratamiento:", error);
    return { error: "Error de conexión con el servidor" };
  }
};

/**
 * Obtiene el dashboard de un tratamiento
 * @param {string} tratamientoId - ID del tratamiento
 * @returns {Promise<Object>}
 */
export const obtenerDashboardTratamiento = async (tratamientoId) => {
  try {
    const headers = await getAuthHeaders();
    const resp = await fetchWrapper(`${API_URL}/api/tratamientos/${tratamientoId}`, {
      method: "GET",
      headers
    });

    const result = await resp.json();
    if (!resp.ok) {
      return { error: result.error || "Error al obtener tratamiento" };
    }
    return result;
  } catch (error) {
    console.error("Error en obtenerDashboardTratamiento:", error);
    return { error: "Error de conexión con el servidor" };
  }
};

/**
 * Obtiene tratamientos de una mascota
 * @param {string} objetoId - ID de la mascota
 * @param {string} estado - Filtro por estado (opcional)
 * @returns {Promise<Object>}
 */
export const obtenerTratamientosMascota = async (objetoId, estado = null) => {
  try {
    const headers = await getAuthHeaders();
    let url = `${API_URL}/api/tratamientos/mascota/${objetoId}`;
    if (estado) {
      url += `?estado=${estado}`;
    }

    const resp = await fetchWrapper(url, {
      method: "GET",
      headers
    });

    const result = await resp.json();
    if (!resp.ok) {
      return { error: result.error || "Error al obtener tratamientos" };
    }
    return result;
  } catch (error) {
    console.error("Error en obtenerTratamientosMascota:", error);
    return { error: "Error de conexión con el servidor" };
  }
};

/**
 * Reporta que el usuario olvidó tomar medicamentos
 * @param {string} tratamientoId - ID del tratamiento
 * @param {string} fecha - Fecha del olvido (YYYY-MM-DD)
 * @param {Array} bloques - Bloques olvidados
 * @returns {Promise<Object>}
 */
export const reportarOlvido = async (tratamientoId, fecha, bloques) => {
  try {
    const headers = await getAuthHeaders();
    const resp = await fetchWrapper(`${API_URL}/api/tratamientos/${tratamientoId}/reportar-olvido`, {
      method: "POST",
      headers,
      body: JSON.stringify({ fecha, bloques })
    });

    const result = await resp.json();
    if (!resp.ok) {
      return { error: result.error || "Error al reportar olvido" };
    }
    return result;
  } catch (error) {
    console.error("Error en reportarOlvido:", error);
    return { error: "Error de conexión con el servidor" };
  }
};

/**
 * Verifica si una entrada del expediente tiene recordatorios
 * @param {string} objetoId - ID de la mascota
 * @param {string} entradaId - ID de la entrada
 * @returns {Promise<Object>}
 */
export const verificarRecordatoriosEntrada = async (objetoId, entradaId) => {
  try {
    const headers = await getAuthHeaders();
    const resp = await fetchWrapper(`${API_URL}/api/tratamientos/entrada/${objetoId}/${entradaId}`, {
      method: "GET",
      headers
    });

    const result = await resp.json();
    if (!resp.ok) {
      return { error: result.error || "Error al verificar recordatorios" };
    }
    return result;
  } catch (error) {
    console.error("Error en verificarRecordatoriosEntrada:", error);
    return { error: "Error de conexión con el servidor" };
  }
};

/**
 * Cancela y elimina un tratamiento activo
 * @param {string} tratamientoId - ID del tratamiento
 * @returns {Promise<Object>}
 */
export const cancelarTratamiento = async (tratamientoId) => {
  try {
    const headers = await getAuthHeaders();
    const resp = await fetchWrapper(`${API_URL}/api/tratamientos/${tratamientoId}/cancelar`, {
      method: "DELETE",
      headers
    });

    const result = await resp.json();
    if (!resp.ok) {
      return { error: result.error || "Error al cancelar tratamiento" };
    }
    return result;
  } catch (error) {
    console.error("Error en cancelarTratamiento:", error);
    return { error: "Error de conexión con el servidor" };
  }
};

/**
 * Reemplaza un tratamiento existente por uno nuevo
 * @param {Object} data - { objetoId, entradaExpedienteId, medicamentos }
 * @returns {Promise<Object>}
 */
export const reemplazarTratamiento = async (data) => {
  try {
    const headers = await getAuthHeaders();
    const resp = await fetchWrapper(`${API_URL}/api/tratamientos/reemplazar`, {
      method: "POST",
      headers,
      body: JSON.stringify(data)
    });

    const result = await resp.json();
    if (!resp.ok) {
      return { error: result.error || "Error al reemplazar tratamiento" };
    }
    return result;
  } catch (error) {
    console.error("Error en reemplazarTratamiento:", error);
    return { error: "Error de conexión con el servidor" };
  }
};
